import { useState, useEffect, useCallback, useRef } from 'react'
import { FileText, ChevronDown, ChevronLeft, ChevronRight, Printer, RefreshCw, Calendar } from 'lucide-react'
import { downloadPdf } from '../../utils/pdfUtils'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { gestaoService } from '../../services/gestaoService'
import leiloesService from '../../services/leiloesService'
import toast from 'react-hot-toast'

const fmt = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const fmtDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '—')

// ── Date helpers ──────────────────────────────────────────────────────────────
const isoToDisplay = (iso) => { if (!iso) return ''; const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}` }
const isoToDate = (iso) => { if (!iso) return null; const [y, m, d] = iso.split('-'); return new Date(Number(y), Number(m) - 1, Number(d)) }
const dateToISO = (date) => { if (!date) return ''; return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` }
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r }
const MONTHS_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro']
const DAYS_PT   = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb']

function DateRangePicker({ dataInicio, dataFim, onChange }) {
  const today = new Date(); today.setHours(0,0,0,0)
  const [open, setOpen]           = useState(false)
  const [pos, setPos]             = useState({ top: 0, left: 0 })
  const [tempStart, setTempStart] = useState(null)
  const [tempEnd, setTempEnd]     = useState(null)
  const [selecting, setSelecting] = useState('start')
  const [hoverDate, setHoverDate] = useState(null)
  const [months, setMonths]       = useState(() => {
    const m = today.getMonth(), y = today.getFullYear()
    return { left: { year: m===0?y-1:y, month: m===0?11:m-1 }, right: { year: y, month: m } }
  })
  const triggerRef = useRef(null)
  const popoverRef = useRef(null)
  useEffect(() => {
    if (!open) return
    const h = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target) &&
          popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])
  const openPicker = () => {
    if (triggerRef.current) { const r = triggerRef.current.getBoundingClientRect(); setPos({ top: r.bottom+6, left: r.left }) }
    setTempStart(isoToDate(dataInicio)); setTempEnd(isoToDate(dataFim)); setSelecting('start'); setOpen(true)
  }
  const shiftMonths = (delta) => setMonths(({ left, right }) => {
    const shift = ({ year, month }) => { const m = month+delta; if (m<0) return {year:year-1,month:11}; if (m>11) return {year:year+1,month:0}; return {year,month:m} }
    return { left: shift(left), right: shift(right) }
  })
  const handleDayClick = (date) => {
    if (selecting==='start' || !tempStart || date<tempStart) { setTempStart(date); setTempEnd(null); setSelecting('end') }
    else { setTempEnd(date); setSelecting('start') }
  }
  const applyPreset = (s, e) => { setTempStart(s); setTempEnd(e); setSelecting('start') }
  const handleApply = () => { if (!tempStart) return; onChange(dateToISO(tempStart), dateToISO(tempEnd||tempStart)); setOpen(false) }
  const presets = [
    { label: 'Hoje',           fn: () => applyPreset(today, today) },
    { label: 'Ontem',          fn: () => { const d=addDays(today,-1); applyPreset(d,d) } },
    { label: 'Últimos 7 dias', fn: () => applyPreset(addDays(today,-6), today) },
    { label: 'Últimos 30 dias',fn: () => applyPreset(addDays(today,-29), today) },
    { label: 'Este mês',       fn: () => applyPreset(new Date(today.getFullYear(),today.getMonth(),1), today) },
    { label: 'Mês passado',    fn: () => applyPreset(new Date(today.getFullYear(),today.getMonth()-1,1), new Date(today.getFullYear(),today.getMonth(),0)) },
    { label: 'Este ano',       fn: () => applyPreset(new Date(today.getFullYear(),0,1), today) },
    { label: 'Ano passado',    fn: () => applyPreset(new Date(today.getFullYear()-1,0,1), new Date(today.getFullYear()-1,11,31)) },
  ]
  const renderMonth = (year, month) => {
    const firstDow=new Date(year,month,1).getDay(), daysInMonth=new Date(year,month+1,0).getDate()
    const cells=[]; for(let i=0;i<firstDow;i++) cells.push(null); for(let d=1;d<=daysInMonth;d++) cells.push(new Date(year,month,d))
    const effectiveEnd = tempEnd || (selecting==='end' && hoverDate ? hoverDate : null)
    return (
      <div className="w-52">
        <div className="grid grid-cols-7 mb-1">{DAYS_PT.map(d=><div key={d} className="text-center text-[11px] text-gray-400 font-medium pb-1">{d}</div>)}</div>
        <div className="grid grid-cols-7">{cells.map((date,i)=>{
          if(!date) return <div key={`e${i}`}/>
          const iso=dateToISO(date), isStart=tempStart&&dateToISO(tempStart)===iso, isEnd=tempEnd&&dateToISO(tempEnd)===iso
          const inRange=tempStart&&effectiveEnd&&date>tempStart&&date<effectiveEnd, isToday=iso===dateToISO(today)
          return <div key={iso} onClick={()=>handleDayClick(date)} onMouseEnter={()=>selecting==='end'&&setHoverDate(date)}
            className={['text-center text-sm py-[5px] cursor-pointer select-none transition-colors',
              isStart||isEnd?'bg-indigo-600 text-white rounded-full font-semibold':inRange?'bg-indigo-100 text-indigo-800':isToday?'text-indigo-600 font-semibold hover:bg-gray-100 rounded-full':'text-gray-700 hover:bg-gray-100 rounded-full'].join(' ')}>{date.getDate()}</div>
        })}</div>
      </div>
    )
  }
  return (
    <div>
      <button ref={triggerRef} onClick={openPicker}
        className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-sm bg-white transition-colors whitespace-nowrap ${open?'border-indigo-400 ring-1 ring-indigo-200':'border-gray-200 hover:border-gray-300'}`}>
        <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0"/>
        <span className={dataInicio?'text-gray-800':'text-gray-400'}>{dataInicio?`${isoToDisplay(dataInicio)} – ${isoToDisplay(dataFim||dataInicio)}`:'Selecionar período'}</span>
      </button>
      {open&&<div ref={popoverRef} style={{position:'fixed',top:pos.top,left:pos.left,zIndex:9999}} className="bg-white rounded-xl shadow-2xl border border-gray-100 p-5 flex gap-0">
        <div className="flex flex-col gap-0.5 pr-4 border-r border-gray-100 mr-5 min-w-[130px]">
          {presets.map(p=><button key={p.label} onClick={p.fn} className="text-left text-sm px-3 py-2 rounded-lg hover:bg-indigo-50 hover:text-indigo-700 text-gray-600 transition-colors whitespace-nowrap">{p.label}</button>)}
        </div>
        <div className="flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <button onClick={()=>shiftMonths(-1)} className="p-1 hover:bg-gray-100 rounded text-gray-500"><ChevronLeft className="w-4 h-4"/></button>
            <div className="flex gap-8">
              <span className="text-sm font-semibold text-gray-800 w-48 text-center">{MONTHS_PT[months.left.month]} {months.left.year}</span>
              <span className="text-sm font-semibold text-gray-800 w-48 text-center">{MONTHS_PT[months.right.month]} {months.right.year}</span>
            </div>
            <button onClick={()=>shiftMonths(1)} className="p-1 hover:bg-gray-100 rounded text-gray-500"><ChevronRight className="w-4 h-4"/></button>
          </div>
          <div className="flex gap-5" onMouseLeave={()=>setHoverDate(null)}>
            {renderMonth(months.left.year,months.left.month)}
            <div className="w-px bg-gray-100 self-stretch"/>
            {renderMonth(months.right.year,months.right.month)}
          </div>
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
            <span className="text-sm text-gray-500">{tempStart?`${isoToDisplay(dateToISO(tempStart))} – ${tempEnd?isoToDisplay(dateToISO(tempEnd)):'...'}`:'Selecione o período'}</span>
            <div className="flex gap-2">
              <button onClick={()=>setOpen(false)} className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancelar</button>
              <button onClick={handleApply} disabled={!tempStart} className="px-4 py-1.5 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-40 font-medium">Aplicar</button>
            </div>
          </div>
        </div>
      </div>}
    </div>
  )
}

const fmtPct = (v) => `${Number(v || 0).toFixed(1)}%`

export default function RelatorioGeral() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id
  const iframeRef = useRef(null)

  const [leiloes, setLeiloes] = useState([])
  const [modoPeriodo, setModoPeriodo] = useState(false)
  const [leilaoId, setLeilaoId] = useState('')
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [loadingL, setLoadingL] = useState(true)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdfModal, setShowPdfModal] = useState(false)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .catch(() => {})
      .finally(() => setLoadingL(false))
  }, [orgId])

  const carregar = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const filtros = {}
      if (!modoPeriodo && leilaoId) filtros.leilao_id = leilaoId
      if (modoPeriodo && dataInicio) filtros.data_inicio = dataInicio
      if (modoPeriodo && dataFim) filtros.data_fim = dataFim
      const res = await gestaoService.dashboard(orgId, filtros)
      setDados(res.data || res)
    } catch {
      toast.error('Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }, [orgId, modoPeriodo, leilaoId, dataInicio, dataFim])

  useEffect(() => {
    if (orgId && (leilaoId || modoPeriodo)) carregar()
  }, [orgId, leilaoId]) // eslint-disable-line

  const handleImprimir = async () => {
    if (!orgId) return
    setLoadingPdf(true)
    try {
      const filtros = {}
      if (!modoPeriodo && leilaoId) filtros.leilao_id = leilaoId
      if (modoPeriodo && dataInicio) filtros.data_inicio = dataInicio
      if (modoPeriodo && dataFim) filtros.data_fim = dataFim
      const res = await gestaoService.relatorioGeralPdf(orgId, filtros)
      const blob = new Blob([res.data], { type: 'application/pdf' })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(blob)
      setPdfUrl(url)
      setShowPdfModal(true)
    } catch {
      toast.error('Erro ao gerar PDF')
    } finally {
      setLoadingPdf(false)
    }
  }

  const closePdfModal = () => { setShowPdfModal(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)
  const filtroInfo = modoPeriodo
    ? `Período: ${dataInicio ? fmtDate(dataInicio) : '...'} a ${dataFim ? fmtDate(dataFim) : '...'}`
    : leilaoAtual ? leilaoAtual.nome : 'Todos os leilões'

  // Extrair dados do dashboard
  const totalReceitas = Number(dados?.total_receitas || dados?.receitas?.total || 0)
  const totalDespesas = Number(dados?.total_despesas || dados?.despesas?.total || 0)
  const resultado = totalReceitas - totalDespesas
  const margem = totalReceitas > 0 ? (resultado / totalReceitas) * 100 : 0

  // Categorias de receitas e despesas
  const categoriasReceitas = dados?.receitas_por_categoria || dados?.receitas?.por_categoria || []
  const categoriasDespesas = dados?.despesas_por_categoria || dados?.despesas?.por_categoria || []

  // Top 5
  const topReceitas = dados?.top_receitas || dados?.maiores_receitas || []
  const topDespesas = dados?.top_despesas || dados?.maiores_despesas || []

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador para gerar o relatório.
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Título */}
      <div className="no-print flex items-center gap-2">
        <FileText className="w-5 h-5 text-blue-600" />
        <h1 className="text-lg font-semibold text-gray-800">Relatório Financeiro Geral</h1>
      </div>

      {/* Filtros */}
      <div className="no-print bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex rounded-lg overflow-hidden border border-gray-200">
            <button onClick={() => setModoPeriodo(false)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${!modoPeriodo ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              Por Leilão
            </button>
            <button onClick={() => setModoPeriodo(true)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${modoPeriodo ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              Por Período
            </button>
          </div>

          {!modoPeriodo ? (
            <div className="flex flex-col gap-1 min-w-[220px]">
              <label className="text-xs text-gray-500 font-medium">Leilão</label>
              <div className="relative">
                <select value={leilaoId} onChange={e => setLeilaoId(e.target.value)} disabled={loadingL}
                  className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100">
                  <option value="">Todos os leilões</option>
                  {leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          ) : (
            <DateRangePicker
              dataInicio={dataInicio}
              dataFim={dataFim}
              onChange={(s, e) => { setDataInicio(s); setDataFim(e) }}
            />
          )}

          <button onClick={carregar}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
            Filtrar
          </button>

          <div className="flex items-end gap-2 ml-auto">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={handleImprimir} disabled={!dados || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> {loadingPdf ? 'Gerando...' : 'Imprimir / PDF'}
            </button>
          </div>
        </div>
      </div>

      {/* Conteúdo imprimível */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : !dados ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          Selecione um leilão ou período para gerar o relatório
        </div>
      ) : (
        <div className="print-page bg-white rounded-xl border border-gray-200 shadow-sm p-8">
          {/* Cabeçalho */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-800 gap-4">
            <div className="w-28 flex-shrink-0">
              {organizadorAtivo?.logo_url
                ? <img src={organizadorAtivo.logo_url} alt="Logo" className="max-h-14 max-w-full object-contain" />
                : <span className="text-xs font-bold text-gray-700">{organizadorAtivo?.name}</span>}
            </div>
            <div className="text-center flex-1">
              <h2 className="text-xl font-black uppercase tracking-wide text-gray-900">Relatório Financeiro Geral</h2>
              <p className="text-sm text-gray-600 mt-0.5">{organizadorAtivo?.name}</p>
              <p className="text-sm text-gray-500 mt-0.5">{filtroInfo}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Emitido em {new Date().toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })} às {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' })}
              </p>
            </div>
            <div className="w-28 flex-shrink-0" />
          </div>

          {/* Cards resumo */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-xs text-green-600 font-medium">Total Receitas</p>
              <p className="text-lg font-bold text-green-800">{fmt(totalReceitas)}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-xs text-red-600 font-medium">Total Despesas</p>
              <p className="text-lg font-bold text-red-800">{fmt(totalDespesas)}</p>
            </div>
            <div className={`border rounded-lg p-4 text-center ${resultado >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}`}>
              <p className={`text-xs font-medium ${resultado >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Resultado</p>
              <p className={`text-lg font-bold ${resultado >= 0 ? 'text-blue-800' : 'text-orange-800'}`}>{fmt(resultado)}</p>
            </div>
            <div className={`border rounded-lg p-4 text-center ${margem >= 0 ? 'bg-indigo-50 border-indigo-200' : 'bg-orange-50 border-orange-200'}`}>
              <p className={`text-xs font-medium ${margem >= 0 ? 'text-indigo-600' : 'text-orange-600'}`}>Margem de Lucro</p>
              <p className={`text-lg font-bold ${margem >= 0 ? 'text-indigo-800' : 'text-orange-800'}`}>{fmtPct(margem)}</p>
            </div>
          </div>

          {/* Receitas por Categoria */}
          {categoriasReceitas.length > 0 && (
            <div className="mb-8">
              <div className="bg-green-100 px-4 py-2 rounded-t-lg">
                <h3 className="text-sm font-bold text-green-800 uppercase">Receitas por Categoria</h3>
              </div>
              <table className="w-full text-left border border-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Categoria</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600 text-center">Qtd Contas</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600 text-right">Valor Total</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600 text-right">% do Total</th>
                  </tr>
                </thead>
                <tbody>
                  {categoriasReceitas.map((cat, i) => (
                    <tr key={cat.nome || i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 text-xs text-gray-700">{cat.nome || 'Sem categoria'}</td>
                      <td className="px-3 py-2 text-xs text-gray-700 text-center">{cat.quantidade || cat.qtd || 0}</td>
                      <td className="px-3 py-2 text-xs text-green-700 font-semibold text-right">{fmt(cat.total || cat.valor)}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 text-right">
                        {fmtPct(totalReceitas > 0 ? ((cat.total || cat.valor || 0) / totalReceitas) * 100 : 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-green-50 border-t border-gray-300">
                  <tr>
                    <td className="px-3 py-2 text-xs font-bold text-gray-700">Total</td>
                    <td className="px-3 py-2 text-xs font-bold text-gray-700 text-center">
                      {categoriasReceitas.reduce((s, c) => s + (c.quantidade || c.qtd || 0), 0)}
                    </td>
                    <td className="px-3 py-2 text-xs font-bold text-green-800 text-right">{fmt(totalReceitas)}</td>
                    <td className="px-3 py-2 text-xs font-bold text-gray-700 text-right">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Despesas por Categoria */}
          {categoriasDespesas.length > 0 && (
            <div className="mb-8">
              <div className="bg-red-100 px-4 py-2 rounded-t-lg">
                <h3 className="text-sm font-bold text-red-800 uppercase">Despesas por Categoria</h3>
              </div>
              <table className="w-full text-left border border-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Categoria</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600 text-center">Qtd Contas</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600 text-right">Valor Total</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600 text-right">% do Total</th>
                  </tr>
                </thead>
                <tbody>
                  {categoriasDespesas.map((cat, i) => (
                    <tr key={cat.nome || i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 text-xs text-gray-700">{cat.nome || 'Sem categoria'}</td>
                      <td className="px-3 py-2 text-xs text-gray-700 text-center">{cat.quantidade || cat.qtd || 0}</td>
                      <td className="px-3 py-2 text-xs text-red-700 font-semibold text-right">{fmt(cat.total || cat.valor)}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 text-right">
                        {fmtPct(totalDespesas > 0 ? ((cat.total || cat.valor || 0) / totalDespesas) * 100 : 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-red-50 border-t border-gray-300">
                  <tr>
                    <td className="px-3 py-2 text-xs font-bold text-gray-700">Total</td>
                    <td className="px-3 py-2 text-xs font-bold text-gray-700 text-center">
                      {categoriasDespesas.reduce((s, c) => s + (c.quantidade || c.qtd || 0), 0)}
                    </td>
                    <td className="px-3 py-2 text-xs font-bold text-red-800 text-right">{fmt(totalDespesas)}</td>
                    <td className="px-3 py-2 text-xs font-bold text-gray-700 text-right">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Top 5 Maiores Receitas */}
          {topReceitas.length > 0 && (
            <div className="mb-8">
              <div className="bg-green-100 px-4 py-2 rounded-t-lg">
                <h3 className="text-sm font-bold text-green-800 uppercase">Top 5 Maiores Receitas</h3>
              </div>
              <table className="w-full text-left border border-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Descrição</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Cliente</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600 text-right">Valor</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {topReceitas.slice(0, 5).map((c, i) => (
                    <tr key={c.id || i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 text-xs text-gray-700">{c.descricao}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">{c.cliente?.nome || c.cliente_nome || '—'}</td>
                      <td className="px-3 py-2 text-xs text-green-700 font-semibold text-right">{fmt(c.valor)}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">{fmtDate(c.data_vencimento || c.data)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Top 5 Maiores Despesas */}
          {topDespesas.length > 0 && (
            <div className="mb-8">
              <div className="bg-red-100 px-4 py-2 rounded-t-lg">
                <h3 className="text-sm font-bold text-red-800 uppercase">Top 5 Maiores Despesas</h3>
              </div>
              <table className="w-full text-left border border-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Descrição</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600 text-right">Valor</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {topDespesas.slice(0, 5).map((c, i) => (
                    <tr key={c.id || i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 text-xs text-gray-700">{c.descricao}</td>
                      <td className="px-3 py-2 text-xs text-red-700 font-semibold text-right">{fmt(c.valor)}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">{fmtDate(c.data_vencimento || c.data)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Comparativo */}
          <div className="mb-4">
            <div className="bg-gray-200 px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-800 uppercase">Comparativo</h3>
            </div>
            <div className="border border-gray-200 rounded-b-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                {/* Receitas */}
                <div className="text-center">
                  <p className="text-xs text-gray-500 font-medium mb-1">Receitas</p>
                  <p className="text-2xl font-bold text-green-700">{fmt(totalReceitas)}</p>
                  <div className="mt-2 h-3 bg-green-200 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full"
                      style={{ width: `${totalReceitas + totalDespesas > 0 ? (totalReceitas / (totalReceitas + totalDespesas)) * 100 : 50}%` }} />
                  </div>
                </div>

                {/* Resultado */}
                <div className="text-center">
                  <p className="text-xs text-gray-500 font-medium mb-1">Resultado</p>
                  <p className={`text-3xl font-black ${resultado >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                    {fmt(resultado)}
                  </p>
                  <p className={`text-sm mt-1 ${margem >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                    Margem: {fmtPct(margem)}
                  </p>
                </div>

                {/* Despesas */}
                <div className="text-center">
                  <p className="text-xs text-gray-500 font-medium mb-1">Despesas</p>
                  <p className="text-2xl font-bold text-red-700">{fmt(totalDespesas)}</p>
                  <div className="mt-2 h-3 bg-red-200 rounded-full overflow-hidden">
                    <div className="h-full bg-red-500 rounded-full"
                      style={{ width: `${totalReceitas + totalDespesas > 0 ? (totalDespesas / (totalReceitas + totalDespesas)) * 100 : 50}%` }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal PDF */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ height: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800">Relatório Financeiro Geral</span>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadPdf(pdfUrl, 'Relatorio_Financeiro_Geral.pdf')}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">
                  Salvar PDF
                </button>
                <button onClick={printPdf}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Printer className="w-4 h-4" /> Imprimir
                </button>
                <button onClick={closePdfModal}
                  className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">
                  Fechar
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title="Relatório Financeiro Geral"
                style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
